import React, { useEffect, useState } from 'react';
import api from '../services/api';
import { swal } from '../utils/swal';
import './Documentos.css';

interface Documento {
  id: number;
  tipo_documento_id: number;
  tipo_documento_nombre?: string;
  tipo_documento_codigo?: string;
  entidad_tipo: string;
  entidad_id: number;
  numero_documento: string;
  fecha_emision: string;
  fecha_vencimiento: string | null;
  archivo_path: string;
  estado: string;
  resultado_verificacion: string;
  verificado_por: number | null;
  fecha_verificacion: string | null;
  entidad_nombre?: string;
}

interface Operador {
  id: number;
  tipo_documento: string;
  numero_documento: string;
  nombres: string;
  apellidos: string;
  estado: string;
}

interface TipoDocumento {
  id: number;
  codigo: string;
  nombre: string;
  aplica_a: string;
  obligatorio: boolean;
}

const Documentos: React.FC = () => {
  const [documentos, setDocumentos] = useState<Documento[]>([]);
  const [tiposDocumentos, setTiposDocumentos] = useState<TipoDocumento[]>([]);
  const [operadores, setOperadores] = useState<Operador[]>([]);
  const [loading, setLoading] = useState(true);
  const [filtroCategoria, setFiltroCategoria] = useState('');
  const [filtroTipo, setFiltroTipo] = useState('');
  const [filtroEstado, setFiltroEstado] = useState('');
  const [mostrarModal, setMostrarModal] = useState(false);
  const [documentoSeleccionado, setDocumentoSeleccionado] = useState<Documento | null>(null);

  useEffect(() => {
    cargarTiposDocumentos();
    cargarDocumentos();
    if (filtroCategoria === 'operador' || !filtroCategoria) {
      cargarOperadores();
    }
  }, [filtroCategoria, filtroTipo, filtroEstado]);

  const cargarTiposDocumentos = async () => {
    try {
      const response = await api.get('/documentos/tipos');
      setTiposDocumentos(response.data.data || response.data || []);
    } catch (error) {
      console.error('Error cargando tipos de documentos:', error);
    }
  };

  const cargarOperadores = async () => {
    try {
      const response = await api.get('/operadores', { params: { limit: 1000 } });
      setOperadores(response.data.data || response.data || []);
    } catch (error) {
      console.error('Error cargando operadores:', error);
    }
  };

  const cargarDocumentos = async () => {
    try {
      setLoading(true);
      const params: any = {};
      if (filtroCategoria) params.entidad_tipo = filtroCategoria;
      if (filtroTipo) params.tipo_documento_id = filtroTipo;
      if (filtroEstado) params.estado = filtroEstado;

      const response = await api.get('/documentos', { params });
      setDocumentos(response.data.data || response.data || []);
    } catch (error: any) {
      console.error('Error cargando documentos:', error);
    } finally {
      setLoading(false);
    }
  };

  const verDocumento = (documento: Documento) => {
    setDocumentoSeleccionado(documento);
    setMostrarModal(true);
  };

  const cerrarModal = () => {
    setMostrarModal(false);
    setDocumentoSeleccionado(null);
  };

  const generarPDFHojaVida = async (operadorId: number) => {
    try {
      const response = await api.get(`/documentos/operador/${operadorId}/hoja-vida-pdf`, {
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `hoja-vida-operador-${operadorId}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error: any) {
      await swal.error(error.response?.data?.error || 'Error al generar PDF');
    }
  };

  const generarPDFAcuerdo = async (operadorId: number) => {
    try {
      const response = await api.get(`/documentos/operador/${operadorId}/acuerdo-pdf`, {
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `acuerdo-confidencialidad-${operadorId}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error: any) {
      await swal.error(error.response?.data?.error || 'Error al generar PDF');
    }
  };

  const getEstadoBadge = (estado: string) => {
    const badges: { [key: string]: { text: string; class: string } } = {
      vigente: { text: 'Vigente', class: 'badge-success' },
      vencido: { text: 'Vencido', class: 'badge-danger' },
      por_vencer: { text: 'Por Vencer', class: 'badge-warning' },
      rechazado: { text: 'Rechazado', class: 'badge-danger' }
    };
    const badge = badges[estado] || { text: estado, class: 'badge-secondary' };
    return <span className={`badge ${badge.class}`}>{badge.text}</span>;
  };

  const getCategoriaIcon = (categoria: string) => {
    const icons: { [key: string]: string } = {
      operador: '👤',
      vehiculo: '🚛',
      remolque: '🚚',
      cliente: '🏢'
    };
    return icons[categoria] || '📄';
  };

  if (loading) {
    return <div className="loading">Cargando documentos...</div>;
  }

  return (
    <div className="documentos-page">
      <div className="page-header">
        <div className="header-actions">
          <select
            value={filtroCategoria}
            onChange={(e) => setFiltroCategoria(e.target.value)}
            className="filter-select"
          >
            <option value="">Todas las categorías</option>
            <option value="operador">👤 Operadores</option>
            <option value="vehiculo">🚛 Vehículos</option>
            <option value="remolque">🚚 Remolques</option>
          </select>

          <select
            value={filtroTipo}
            onChange={(e) => setFiltroTipo(e.target.value)}
            className="filter-select"
          >
            <option value="">Todos los tipos</option>
            {tiposDocumentos
              .filter(td => !filtroCategoria || td.aplica_a === filtroCategoria || td.aplica_a === 'ambos')
              .map(td => (
                <option key={td.id} value={td.id}>
                  {td.nombre}
                </option>
              ))}
          </select>

          <select
            value={filtroEstado}
            onChange={(e) => setFiltroEstado(e.target.value)}
            className="filter-select"
          >
            <option value="">Todos los estados</option>
            <option value="vigente">Vigente</option>
            <option value="vencido">Vencido</option>
            <option value="por_vencer">Por Vencer</option>
            <option value="rechazado">Rechazado</option>
          </select>
        </div>
      </div>

      <div className="documentos-grid">
        {/* Categoría: Operadores */}
        <div className="categoria-section">
          <h2>👤 Documentos de Operadores</h2>
          <div className="operadores-lista">
            {operadores
              .filter(op => {
                // Filtrar solo operadores que tienen documentos
                const tieneDocumentos = documentos.some(doc => 
                  doc.entidad_tipo === 'operador' && doc.entidad_id === op.id
                );
                return tieneDocumentos;
              })
              .map(operador => {
                // Separar documentos: hoja de vida/acuerdo vs otros documentos
                const todosDocumentosOperador = documentos.filter(
                  doc => doc.entidad_tipo === 'operador' && doc.entidad_id === operador.id
                );
                
                const documentosSubidos = todosDocumentosOperador.filter(doc => {
                  const codigo = doc.tipo_documento_codigo || '';
                  const nombre = (doc.tipo_documento_nombre || '').toLowerCase();
                  return codigo !== 'HOJA_VIDA' && 
                         codigo !== 'ACUERDO_CONFIDENCIALIDAD' &&
                         !nombre.includes('hoja de vida') &&
                         !nombre.includes('acuerdo de confidencialidad');
                });
                
                return (
                  <div key={operador.id} className="operador-row">
                    <div className="operador-info">
                      <div className="operador-nombre">
                        <strong>{operador.nombres} {operador.apellidos}</strong>
                      </div>
                      <div className="operador-documento">
                        {operador.tipo_documento} {operador.numero_documento}
                      </div>
                    </div>
                    <div className="operador-actions">
                      <div className="documentos-principales">
                        <button
                          onClick={() => generarPDFHojaVida(operador.id)}
                          className="btn-pdf"
                          title="Descargar Hoja de Vida PDF"
                        >
                          📄 Ver Hoja de Vida
                        </button>
                        <button
                          onClick={() => generarPDFAcuerdo(operador.id)}
                          className="btn-pdf"
                          title="Descargar Acuerdo de Confidencialidad PDF"
                        >
                          📋 Ver Acuerdo
                        </button>
                      </div>
                      {documentosSubidos.length > 0 && (
                        <div className="documentos-subidos">
                          <strong>Documentos subidos ({documentosSubidos.length}):</strong>
                          {documentosSubidos.map(doc => (
                            <div key={doc.id} className="documento-subido-item">
                              <span>{doc.tipo_documento_nombre}</span>
                              <button
                                onClick={async () => {
                                  try {
                                    const response = await api.get(`/documentos/${doc.id}/pdf`, {
                                      responseType: 'blob'
                                    });
                                    const url = window.URL.createObjectURL(new Blob([response.data]));
                                    const link = document.createElement('a');
                                    link.href = url;
                                    link.setAttribute('download', `documento-${doc.id}.pdf`);
                                    document.body.appendChild(link);
                                    link.click();
                                    link.remove();
                                    window.URL.revokeObjectURL(url);
                                  } catch (error) {
                                    console.error('Error generando PDF:', error);
                                    alert('Error al generar PDF');
                                  }
                                }}
                                className="btn-link-small"
                              >
                                📄 PDF
                              </button>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            {operadores.filter(op => 
              documentos.some(doc => doc.entidad_tipo === 'operador' && doc.entidad_id === op.id)
            ).length === 0 && (
              <p className="empty-categoria">No hay documentos de operadores</p>
            )}
          </div>
        </div>

        {/* Categoría: Vehículos */}
        <div className="categoria-section">
          <h2>🚛 Documentos de Vehículos</h2>
          <div className="documentos-categoria">
            {documentos
              .filter(doc => doc.entidad_tipo === 'vehiculo')
              .map(doc => (
                <div key={doc.id} className="documento-card">
                  <div className="documento-header">
                    <h3>{doc.tipo_documento_nombre || 'Documento'}</h3>
                    {getEstadoBadge(doc.estado)}
                  </div>
                  <div className="documento-info">
                    <p><strong>Vehículo ID:</strong> {doc.entidad_id}</p>
                    <p><strong>Número:</strong> {doc.numero_documento}</p>
                    {doc.fecha_vencimiento && (
                      <p><strong>Vence:</strong> {new Date(doc.fecha_vencimiento).toLocaleDateString()}</p>
                    )}
                  </div>
                  <div className="documento-actions">
                    <button
                      onClick={() => verDocumento(doc)}
                      className="btn-link"
                    >
                      👁️ Ver Detalles
                    </button>
                  </div>
                </div>
              ))}
            {documentos.filter(doc => doc.entidad_tipo === 'vehiculo').length === 0 && (
              <p className="empty-categoria">No hay documentos de vehículos</p>
            )}
          </div>
        </div>

        {/* Categoría: Remolques */}
        <div className="categoria-section">
          <h2>🚚 Documentos de Remolques</h2>
          <div className="documentos-categoria">
            {documentos
              .filter(doc => doc.entidad_tipo === 'remolque')
              .map(doc => (
                <div key={doc.id} className="documento-card">
                  <div className="documento-header">
                    <h3>{doc.tipo_documento_nombre || 'Documento'}</h3>
                    {getEstadoBadge(doc.estado)}
                  </div>
                  <div className="documento-info">
                    <p><strong>Remolque ID:</strong> {doc.entidad_id}</p>
                    <p><strong>Número:</strong> {doc.numero_documento}</p>
                    {doc.fecha_vencimiento && (
                      <p><strong>Vence:</strong> {new Date(doc.fecha_vencimiento).toLocaleDateString()}</p>
                    )}
                  </div>
                  <div className="documento-actions">
                    <button
                      onClick={() => verDocumento(doc)}
                      className="btn-link"
                    >
                      👁️ Ver Detalles
                    </button>
                  </div>
                </div>
              ))}
            {documentos.filter(doc => doc.entidad_tipo === 'remolque').length === 0 && (
              <p className="empty-categoria">No hay documentos de remolques</p>
            )}
          </div>
        </div>
      </div>

      {mostrarModal && documentoSeleccionado && (
        <div className="modal-overlay" onClick={cerrarModal}>
          <div className="modal-content modal-documento" onClick={(e) => e.stopPropagation()}>
            <h2>Detalles del Documento</h2>
            <div className="documento-detalles">
              <div className="detalle-item">
                <strong>Tipo:</strong>
                <span>{documentoSeleccionado.tipo_documento_nombre || 'N/A'}</span>
              </div>
              <div className="detalle-item">
                <strong>Categoría:</strong>
                <span>{getCategoriaIcon(documentoSeleccionado.entidad_tipo)} {documentoSeleccionado.entidad_tipo}</span>
              </div>
              <div className="detalle-item">
                <strong>Número:</strong>
                <span>{documentoSeleccionado.numero_documento}</span>
              </div>
              <div className="detalle-item">
                <strong>Estado:</strong>
                <span>{getEstadoBadge(documentoSeleccionado.estado)}</span>
              </div>
              <div className="detalle-item">
                <strong>Fecha de Emisión:</strong>
                <span>{new Date(documentoSeleccionado.fecha_emision).toLocaleDateString()}</span>
              </div>
              {documentoSeleccionado.fecha_vencimiento && (
                <div className="detalle-item">
                  <strong>Fecha de Vencimiento:</strong>
                  <span>{new Date(documentoSeleccionado.fecha_vencimiento).toLocaleDateString()}</span>
                </div>
              )}
              <div className="detalle-item">
                <strong>Verificación:</strong>
                <span>{documentoSeleccionado.resultado_verificacion || 'Pendiente'}</span>
              </div>
            </div>
            <div className="modal-actions">
              <button onClick={cerrarModal} className="btn-secondary">
                Cerrar
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Documentos;

